import { useState, useCallback } from 'react'
import { router } from '@inertiajs/react'
import { User, Meeting } from '../../types'
import { sortPeople } from '../../utils/peopleSort'

interface Props {
  meeting: Meeting
  members: User[]
  readOnly?: boolean
}

export default function SectionBizDev({ meeting, members, readOnly }: Props) {
  const existingNotes = meeting.business_development_notes || {}
  const [notes, setNotes] = useState<Record<string, string>>(existingNotes)
  const [dimmed, setDimmed] = useState<Record<string, boolean>>(() => {
    const d: Record<string, boolean> = {}
    Object.entries(existingNotes).forEach(([id, val]) => {
      if (val === 'Nothing to report') d[id] = true
    })
    return d
  })

  // Task #613: shared alphabetical rule (first name, then last name).
  const sortedMembers = sortPeople(members)

  const saveNotes = useCallback((updatedNotes: Record<string, string>) => {
    router.put(`/meetings/${meeting.id}/update_notes`, {
      business_development_notes: updatedNotes,
    }, { preserveScroll: true, preserveState: true })
  }, [meeting.id])

  const handleBlur = (_userId: number) => {
    saveNotes(notes)
  }

  const handleNothingToReport = (userId: number) => {
    const key = String(userId)
    const updated = { ...notes, [key]: 'Nothing to report' }
    setNotes(updated)
    setDimmed(prev => ({ ...prev, [key]: true }))
    saveNotes(updated)
  }

  const handleInputChange = (userId: number, value: string) => {
    const key = String(userId)
    setNotes(prev => ({ ...prev, [key]: value }))
    if (dimmed[key] && value !== 'Nothing to report') {
      setDimmed(prev => ({ ...prev, [key]: false }))
    }
  }

  const getInitials = (user: User) => {
    return `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase()
  }

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-xl font-semibold text-text-primary tracking-wide uppercase mb-2">Business Development</h3>
        <p className="text-base text-text-muted">Biz dev + voice of the customer.</p>
      </div>

      <div className="bg-charcoal/10 rounded-lg p-5">
        <p className="text-text-primary font-medium text-base leading-relaxed">
          "Brief headlines on Biz Dev and Voice of the Customer"
        </p>
      </div>

      <div className="space-y-2">
        {sortedMembers.map(member => {
          const key = String(member.id)
          const isDimmed = dimmed[key] || false

          return (
            <div
              key={member.id}
              className="flex items-center gap-3 bg-white border border-stone/20 rounded-lg px-4 py-3 transition-opacity"
              style={{ opacity: isDimmed ? 0.5 : 1 }}
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: '#351E1D', color: 'white', fontFamily: 'Montserrat, sans-serif', fontSize: '12px', fontWeight: 600 }}
              >
                {getInitials(member)}
              </div>
              <span
                className="flex-shrink-0"
                style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '15px', fontWeight: 600, color: '#1B1B1B', minWidth: '120px' }}
              >
                {member.full_name}
              </span>
              <input
                type="text"
                value={notes[key] || ''}
                onChange={(e) => handleInputChange(member.id, e.target.value)}
                onBlur={() => handleBlur(member.id)}
                maxLength={150}
                placeholder="Biz dev headline this week..."
                disabled={readOnly}
                className="flex-1 border-0 bg-transparent text-sm text-text-primary placeholder-text-muted focus:outline-none focus:ring-0 disabled:opacity-50"
                style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '14px' }}
              />
              {!readOnly && (
                <button
                  onClick={() => handleNothingToReport(member.id)}
                  className="flex-shrink-0 px-2 py-1 rounded-md hover:bg-stone/10 transition-colors text-text-muted hover:text-text-muted whitespace-nowrap"
                  style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '12px', fontWeight: 500 }}
                >
                  Nothing to report
                </button>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
